import {
	Button,
	Checkbox,
	Group,
	Modal,
	TagsInput,
	Text,
} from "@mantine/core";
import { useEffect, useState } from "react";
import { isValidEmail } from "../../utils/validation";

interface EmailReportModalProps {
	opened: boolean;
	accountName: string;
	reportType: "billing" | "status";
	loading: boolean;
	onClose: () => void;
	onSubmit: (
		emails: string[],
		scheduleMonthly: boolean,
		sendNow: boolean
	) => void;
}

function EmailReportModal({
	opened,
	accountName,
	reportType,
	loading,
	onClose,
	onSubmit,
}: EmailReportModalProps) {
	const [emails, setEmails] = useState<string[]>([]);
	const [scheduleMonthly, setScheduleMonthly] = useState(false);
	const [sendNow, setSendNow] = useState(true);

	useEffect(() => {
		if (opened) {
			setEmails([]);
			setScheduleMonthly(false);
			setSendNow(true);
		}
	}, [opened]);

	const invalidEmails = emails.filter((email) => !isValidEmail(email));
	const canSubmit = emails.length > 0 && invalidEmails.length === 0;

	const handleClose = () => {
		if (!loading) onClose();
	};

	return (
		<Modal
			opened={opened}
			onClose={handleClose}
			title={
				reportType === "status" ? "Email Status Report" : "Email License Report"
			}
			centered>
			<Text
				size="sm"
				mb="md">
				Enter the email addresses to send the{" "}
				{reportType === "status" ? "status" : "license"} report for "
				{accountName}".
			</Text>
			<TagsInput
				label="Email Addresses"
				placeholder="Type an email and press Enter"
				value={emails}
				onChange={setEmails}
				error={
					invalidEmails.length > 0
						? `Invalid email format: ${invalidEmails.join(", ")}`
						: null
				}
				clearable
				disabled={loading}
				data-autofocus
			/>
			<Checkbox
				label="Send this report automatically on the 1st of every month"
				checked={scheduleMonthly}
				onChange={(e) => setScheduleMonthly(e.currentTarget.checked)}
				disabled={loading}
				mt="md"
			/>
			{scheduleMonthly && (
				<Checkbox
					label="Also send the report now"
					checked={sendNow}
					onChange={(e) => setSendNow(e.currentTarget.checked)}
					disabled={loading}
					mt="xs"
					ml="lg"
				/>
			)}
			<Group
				justify="flex-end"
				mt="md">
				<Button
					variant="default"
					onClick={handleClose}
					disabled={loading}>
					Cancel
				</Button>
				<Button
					color="violet"
					onClick={() => onSubmit(emails, scheduleMonthly, sendNow)}
					loading={loading}
					disabled={!canSubmit}>
					{scheduleMonthly && !sendNow ? "Schedule" : "Send Email"}
				</Button>
			</Group>
		</Modal>
	);
}

export default EmailReportModal;
